import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useInstituteAuth } from "../../context/InstituteAuthContext";
import { toast } from "react-toastify";
import ConfirmationModal from "../../common/ConfirmationModal";
import {
  FaUsers,
  FaClock,
  FaUserCheck,
  FaUserTimes,
  FaPlus,
  FaEye,
  FaBuilding,
  FaGamepad,
  FaChartBar,
  FaSignOutAlt,
  FaUserGraduate,
  FaHistory,
} from "react-icons/fa";

const InstituteOwnerDashboard = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [formerMembers, setFormerMembers] = useState([]);
  const [arenas, setArenas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const { instituteUser, logoutInstitute } = useInstituteAuth();
  const navigate = useNavigate();
  const instituteId = instituteUser?.id;

  // Helper function to get display name from user data
  const getDisplayName = (user) => {
    if (user.user_name) return user.user_name;
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown User";
  };

  // Helper function to get initials for avatar
  const getInitials = (user) => {
    const displayName = getDisplayName(user);
    return displayName.charAt(0).toUpperCase();
  };

  useEffect(() => {
    if (instituteId) {
      fetchPendingRequests();
      fetchActiveMembers();
      fetchFormerMembers();
      fetchArenas();
    } else {
      setLoading(false);
    }
  }, [instituteId]);

  const fetchPendingRequests = async () => {
    if (!instituteId) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/pending-requests`,
        { withCredentials: true }
      );
      setPendingRequests(response.data);
    } catch (error) {
      console.error("Error fetching pending requests:", error);
    }
  };

  const fetchActiveMembers = async () => {
    if (!instituteId) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/active-members`,
        { withCredentials: true }
      );
      setActiveMembers(response.data);
    } catch (error) {
      console.error("Error fetching active members:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormerMembers = async () => {
    if (!instituteId) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/former-members`,
        { withCredentials: true }
      );
      setFormerMembers(response.data);
    } catch (error) {
      console.error("Error fetching former members:", error);
    }
  };

  const fetchArenas = async () => {
    if (!instituteId) return;

    console.log(instituteId);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/arenas`,
        { withCredentials: true }
      );
      console.log(response.data);
      setArenas(response.data);
    } catch (error) {
      console.error("Error fetching arenas:", error);
    }
  };

  const handleRequestAction = async (requestId, action) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/requests/${requestId}/status`,
        {
          status: action === "accept" ? "accepted" : "rejected",
        },
        { withCredentials: true }
      );

      toast.success(`Request ${action}ed successfully!`);
      fetchPendingRequests();
      fetchActiveMembers();
      fetchFormerMembers();
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      toast.error(`Error ${action}ing request`);
    }
  };

  const handleRemoveMember = (member) => {
    setSelectedMember(member);
    setModalAction("remove");
    setShowConfirmModal(true);
  };

  const handleConfirmRemoveMember = async () => {
    if (!selectedMember) return;

    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/leave/${selectedMember.id}`,
        {},
        { withCredentials: true }
      );
      toast.success("Member removed successfully!");
      fetchActiveMembers();
      fetchFormerMembers();
      setShowConfirmModal(false);
      setSelectedMember(null);
      setModalAction(null);
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Error removing member");
      setShowConfirmModal(false);
      setSelectedMember(null);
      setModalAction(null);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setSelectedMember(null);
    setModalAction(null);
  };

  const handleLogout = async () => {
    try {
      await logoutInstitute();
      toast.success("Logged out successfully!");
      navigate("/institute-dashboard");
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Error during logout");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#0071bc] mx-auto"></div>
          <p className="mt-6 text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!instituteId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white p-12 rounded-3xl shadow-2xl max-w-md w-full">
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Dsportshub Logo"
              className="h-16 mx-auto mb-6"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Institute Login Required
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            You need to be logged in as an institute owner to access this
            dashboard.
          </p>
          <a
            href="/institute-login"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0071bc] text-white font-semibold rounded-xl shadow-lg hover:bg-[#005a94] transition-all duration-300 transform hover:scale-105"
          >
            <FaUserGraduate />
            Institute Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-6">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="Dsportshub Logo" className="h-12" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Institute Dashboard
                </h1>
                <p className="text-gray-600 text-sm">Manage your institute</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <button
                onClick={() => navigate("/institute-profile")}
                className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-indigo-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
              >
                <FaUserGraduate className="w-4 h-4" />
                Profile
              </button>
              <button
                onClick={() => navigate("/institute-add-game")}
                className="bg-green-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
              >
                <FaGamepad className="w-4 h-4" />
                Add Game
              </button>
              <button
                onClick={() => navigate("/institute-view-game")}
                className="bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-purple-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
              >
                <FaEye className="w-4 h-4" />
                View Scorecards
              </button>
              <button
                onClick={() => navigate("/register-arena/institute")}
                className="bg-[#0071bc] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#005a94] transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
              >
                <FaBuilding className="w-4 h-4" />
                Add Arena
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
              >
                <FaSignOutAlt className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow-xl rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <FaClock className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pending Requests
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {pendingRequests.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow-xl rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FaUserCheck className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Members
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {activeMembers.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow-xl rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <FaHistory className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Former Members
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {formerMembers.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow-xl rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FaBuilding className="w-6 h-6 text-[#0071bc]" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Arenas
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {arenas.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <nav className="flex flex-col sm:flex-row">
              <button
                onClick={() => setActiveTab("pending")}
                className={`w-full sm:flex-1 py-4 px-6 text-sm font-medium transition-all duration-300 ${
                  activeTab === "pending"
                    ? "bg-[#0071bc] text-white shadow-lg"
                    : "text-gray-500 hover:text-[#0071bc] hover:bg-blue-50"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FaClock />
                  Pending Requests ({pendingRequests.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("members")}
                className={`w-full sm:flex-1 py-4 px-6 text-sm font-medium transition-all duration-300 ${
                  activeTab === "members"
                    ? "bg-[#0071bc] text-white shadow-lg"
                    : "text-gray-500 hover:text-[#0071bc] hover:bg-blue-50"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FaUserCheck />
                  Active Members ({activeMembers.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("former")}
                className={`w-full sm:flex-1 py-4 px-6 text-sm font-medium transition-all duration-300 ${
                  activeTab === "former"
                    ? "bg-[#0071bc] text-white shadow-lg"
                    : "text-gray-500 hover:text-[#0071bc] hover:bg-blue-50"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FaHistory />
                  Former Members ({formerMembers.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("arenas")}
                className={`w-full sm:flex-1 py-4 px-6 text-sm font-medium transition-all duration-300 ${
                  activeTab === "arenas"
                    ? "bg-[#0071bc] text-white shadow-lg"
                    : "text-gray-500 hover:text-[#0071bc] hover:bg-blue-50"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FaBuilding />
                  Arenas ({arenas.length})
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Pending Requests Tab */}
        {activeTab === "pending" && (
          <div className="bg-white shadow-xl rounded-2xl border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                <FaClock className="text-yellow-600" />
                Pending Affiliation Requests
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Review and manage incoming membership requests
              </p>
            </div>

            {pendingRequests.length === 0 ? (
              <div className="p-12 text-center">
                <FaClock className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No pending requests
                </h3>
                <p className="text-gray-500">
                  All membership requests have been processed
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {pendingRequests.map((request) => (
                  <div
                    key={request.id}
                    className="p-6 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                            <span className="text-lg font-bold text-white">
                              {getInitials(request)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-gray-900">
                            {getDisplayName(request)}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            <span>📧</span>
                            {request.user_email}
                          </div>
                          {request.verification_id && (
                            <div className="text-sm text-blue-600 font-medium flex items-center gap-1 mt-1">
                              <span>🔑</span>
                              ID: {request.verification_id}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <span>📅</span>
                            Requested on{" "}
                            {new Date(request.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 sm:space-x-3 space-x-0 w-full md:w-auto">
                        <button
                          onClick={() =>
                            handleRequestAction(request.id, "accept")
                          }
                          className="bg-green-600 text-white px-6 py-2 rounded-xl text-sm font-medium hover:bg-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
                        >
                          <FaUserCheck />
                          Accept
                        </button>
                        <button
                          onClick={() =>
                            handleRequestAction(request.id, "reject")
                          }
                          className="bg-red-600 text-white px-6 py-2 rounded-xl text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
                        >
                          <FaUserTimes />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active Members Tab */}
        {activeTab === "members" && (
          <div className="bg-white shadow-xl rounded-2xl border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                <FaUserCheck className="text-green-600" />
                Active Members
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage your current institute members
              </p>
            </div>

            {activeMembers.length === 0 ? (
              <div className="p-12 text-center">
                <FaUsers className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No active members
                </h3>
                <p className="text-gray-500">
                  No members have joined your institute yet
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {activeMembers.map((member) => (
                  <div
                    key={member.id}
                    className="p-6 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
                            <span className="text-lg font-bold text-white">
                              {getInitials(member)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-gray-900">
                            {getDisplayName(member)}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            <span>📧</span>
                            {member.user_email}
                          </div>
                          {member.verification_id && (
                            <div className="text-sm text-blue-600 font-medium flex items-center gap-1 mt-1">
                              <span>🔑</span>
                              ID: {member.verification_id}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <span>📅</span>
                            Joined on{" "}
                            {new Date(member.joined_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 sm:space-x-3 space-x-0 w-full md:w-auto">
                        <span className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                          Active
                        </span>
                        <button
                          onClick={() => handleRemoveMember(member)}
                          className="bg-red-600 text-white px-6 py-2 rounded-xl text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
                        >
                          <FaUserTimes />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Former Members Tab */}
        {activeTab === "former" && (
          <div className="bg-white shadow-xl rounded-2xl border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                <FaHistory className="text-gray-600" />
                Former Members
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                View members who have left the institute
              </p>
            </div>

            {formerMembers.length === 0 ? (
              <div className="p-12 text-center">
                <FaHistory className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No former members
                </h3>
                <p className="text-gray-500">
                  No members have left your institute
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {formerMembers.map((member) => (
                  <div
                    key={member.id}
                    className="p-6 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center shadow-lg">
                            <span className="text-lg font-bold text-white">
                              {getInitials(member)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-gray-900">
                            {getDisplayName(member)}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            <span>📧</span>
                            {member.user_email}
                          </div>
                          {member.verification_id && (
                            <div className="text-sm text-blue-600 font-medium flex items-center gap-1 mt-1">
                              <span>🔑</span>
                              ID: {member.verification_id}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <span>📅</span>
                              Joined:{" "}
                              {new Date(member.joined_at).toLocaleDateString()}
                            </span>
                            {member.left_at && (
                              <span className="flex items-center gap-1">
                                <span>🚪</span>
                                Left:{" "}
                                {new Date(member.left_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <span className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                          Former Member
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Arenas Tab */}
        {activeTab === "arenas" && (
          <div className="bg-white shadow-xl rounded-2xl border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                <FaBuilding className="text-[#0071bc]" />
                Institute Arenas
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage your institute's sports facilities
              </p>
            </div>

            {arenas.length === 0 ? (
              <div className="p-12 text-center">
                <FaBuilding className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No arenas registered yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Start by adding your first sports arena
                </p>
                <button
                  onClick={() => navigate("/register-arena/institute")}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#0071bc] text-white font-semibold rounded-xl shadow-lg hover:bg-[#005a94] transition-all duration-300 transform hover:scale-105"
                >
                  <FaPlus />
                  Add Your First Arena
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {arenas.map((arena) => (
                  <div
                    key={arena.id}
                    className="p-6 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
                      <div className="flex items-start space-x-4 w-full">
                        {arena.logo ? (
                          <img
                            src={`data:image/jpeg;base64,${arena.logo}`}
                            alt={arena.Arena_name}
                            className="w-20 h-20 rounded-xl object-cover border-2 border-gray-200 shadow-lg"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center shadow-lg">
                            <FaBuilding className="w-8 h-8 text-[#0071bc]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {arena.Arena_name}
                          </h3>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              <span>📧</span>
                              {arena.email}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <span>📍</span>
                              {arena.city && arena.country
                                ? `${arena.city}, ${arena.country}`
                                : "Location not specified"}
                            </p>
                          </div>
                          <div className="flex items-center mt-3 space-x-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                arena.is_enabled
                                  ? "bg-green-100 text-green-800 border border-green-200"
                                  : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                              }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full mr-2 ${
                                  arena.is_enabled
                                    ? "bg-green-500 animate-pulse"
                                    : "bg-yellow-500"
                                }`}
                              ></div>
                              {arena.is_enabled
                                ? "Approved"
                                : "Pending Approval"}
                            </span>
                            {arena.facilities_count && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                🏟️ {arena.facilities_count} Facilities
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-3 w-full lg:w-auto">
                        <button
                          onClick={() => navigate(`/arena-details/${arena.id}`)}
                          className="px-6 py-2 bg-[#0071bc] text-white text-sm font-medium rounded-xl hover:bg-[#005a94] transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full lg:w-auto text-center justify-center"
                        >
                          <FaEye />
                          View Details
                        </button>
                        {arena.is_enabled && (
                          <button
                            onClick={() => {
                              navigate("/LoginArena", {
                                state: { email: arena.email },
                              });
                            }}
                            className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 w-full lg:w-auto text-center justify-center"
                          >
                            <FaChartBar />
                            Manage Arena
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal for Remove Member */}
      <ConfirmationModal
        isOpen={showConfirmModal && modalAction === "remove"}
        title="Remove Member"
        message={`Are you sure you want to remove ${selectedMember?.first_name} ${selectedMember?.last_name} from your institute?`}
        onConfirm={handleConfirmRemoveMember}
        onCancel={handleCloseModal}
        confirmText="Yes, Remove"
        cancelText="Keep Member"
        confirmButtonClass="bg-red-600 text-white hover:bg-red-700"
      />
    </div>
  );
};

export default InstituteOwnerDashboard;
